/**
 * FRODO Configuration Panel
 * 
 * Component for configuring FRODO optimization settings in the simulation panel.
 * Follows patterns from SimulationRunner.tsx for consistent styling.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Tooltip,
} from '@mui/material';

import type { FrodoConfig } from '../../types/frodo';
import { useFixedRoutes } from '../../context/FixedRoutesContext';
import FrodoRouteSelectionModal from './FrodoRouteSelectionModal';

import { Icon } from '@liftango/ui-icons';
interface FrodoConfigPanelProps {
  config: FrodoConfig;
  onConfigChange: (config: FrodoConfig) => void;
  hideEnableToggle?: boolean;
}

export default function FrodoConfigPanel({ config, onConfigChange, hideEnableToggle = false }: FrodoConfigPanelProps) {
  const { t } = useTranslation('simulation');
  const { routes } = useFixedRoutes();
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
  const [maxDistanceError, setMaxDistanceError] = useState<string | null>(null);
  const [maxWaitTimeError, setMaxWaitTimeError] = useState<string | null>(null);

  // Validate maxDistanceToStop
  useEffect(() => {
    if (config.maxDistanceToStop <= 0) {
      setMaxDistanceError(t('setup.frodo.maxDistanceError'));
    } else {
      setMaxDistanceError(null);
    }
  }, [config.maxDistanceToStop, t]);

  // Validate maxWaitTimeMinutes
  useEffect(() => {
    if (config.maxWaitTimeMinutes <= 0) {
      setMaxWaitTimeError(t('setup.frodo.maxWaitTimeError'));
    } else {
      setMaxWaitTimeError(null);
    }
  }, [config.maxWaitTimeMinutes, t]);

  const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onConfigChange({
      ...config,
      enabled: event.target.checked,
    });
  };

  const handleMaxDistanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      onConfigChange({
        ...config,
        maxDistanceToStop: value,
      });
    }
  };

  const handleExcludeWheelchairChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onConfigChange({
      ...config,
      excludeWheelchairUsers: event.target.checked,
    });
  };

  const handleMaxWaitTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      onConfigChange({
        ...config,
        maxWaitTimeMinutes: value,
      });
    }
  };

  const handleAllowTransfersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onConfigChange({
      ...config,
      allowTransfers: event.target.checked,
    });
  };

  const handleRouteSelectionChange = (selectedRouteIds: string[]) => {
    onConfigChange({
      ...config,
      selectedRouteIds,
    });
  };

  const selectedRoutesCount = config.selectedRouteIds.length;
  const hasRoutes = routes.length > 0;
  const hasSelectedRoutes = selectedRoutesCount > 0;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Icon size={20} name="gear" style={{ marginRight: 8, color: '#ef7335' }} />
          <Typography variant="h6">
            {t('setup.frodo.title')}
          </Typography>
        </Box>

        {/* Enable/Disable Toggle */}
        {!hideEnableToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={handleEnabledChange}
                color="primary"
              />
            }
            label={t('setup.frodo.enable')}
            sx={{ mb: 2 }}
          />
        )}

        {config.enabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Configure Fixed Routes Button */}
            <Button
              variant="outlined"
              onClick={() => setIsRouteModalOpen(true)}
              disabled={!hasRoutes}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('setup.frodo.configureRoutes')}
              {hasSelectedRoutes && (
                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  {t('setup.frodo.routesSelected', { count: selectedRoutesCount })}
                </Typography>
              )}
            </Button>

            {/* Warning if no routes selected */}
            {config.enabled && !hasSelectedRoutes && (
              <Alert severity="warning">
                {t('setup.frodo.noRoutesWarning')}
              </Alert>
            )}

            {/* Maximum Distance to Stop */}
            <TextField
              label={t('setup.frodo.maxDistance')}
              type="number"
              value={config.maxDistanceToStop}
              onChange={handleMaxDistanceChange}
              error={!!maxDistanceError}
              helperText={maxDistanceError || t('setup.frodo.maxDistanceHelp')}
              fullWidth
              inputProps={{ min: 1, step: 10 }}
              sx={{ mb: maxDistanceError ? 0 : 2 }}
            />

            {/* Exclude Wheelchair Users */}
            <FormControlLabel
              control={
                <Switch
                  checked={config.excludeWheelchairUsers}
                  onChange={handleExcludeWheelchairChange}
                  color="primary"
                />
              }
              label={t('setup.frodo.excludeWheelchair')}
            />

            {/* Maximum Wait Time */}
            <TextField
              label={t('setup.frodo.maxWaitTime')}
              type="number"
              value={config.maxWaitTimeMinutes}
              onChange={handleMaxWaitTimeChange}
              error={!!maxWaitTimeError}
              helperText={maxWaitTimeError || t('setup.frodo.maxWaitTimeHelp')}
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              sx={{ mb: maxWaitTimeError ? 0 : 2 }}
            />

            {/* Allow Transfers */}
            <Tooltip
              title={t('setup.frodo.allowTransfersTooltip')}
              arrow
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={config.allowTransfers}
                    onChange={handleAllowTransfersChange}
                    color="primary"
                  />
                }
                label={t('setup.frodo.allowTransfers')}
              />
            </Tooltip>
          </Box>
        )}
      </CardContent>

      {/* Route Selection Modal */}
      <FrodoRouteSelectionModal
        open={isRouteModalOpen}
        onClose={() => setIsRouteModalOpen(false)}
        selectedRouteIds={config.selectedRouteIds}
        onSelectionChange={handleRouteSelectionChange}
      />
    </Card>
  );
}

